import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Bell, Settings, Download, TrendingUp, Wallet, PieChart as PieIcon,
  Activity, FileText, ShieldCheck, LifeBuoy, ArrowDownRight, ArrowUpRight
} from "lucide-react";
import {
  AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";
import { portfolio } from "../lib/data";

const tabs = [
  { id: "portfolio", label: "Portfolio", Icon: PieIcon },
  { id: "holdings", label: "Holdings", Icon: TrendingUp },
  { id: "sips", label: "SIPs", Icon: Activity },
  { id: "txn", label: "Transactions", Icon: FileText },
  { id: "kyc", label: "KYC", Icon: ShieldCheck },
  { id: "support", label: "Support", Icon: LifeBuoy },
];

const fmt = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export default function ClientDashboard() {
  const [tab, setTab] = useState("portfolio");
  return (
    <div className="min-h-screen bg-[#020617] flex flex-col" data-testid="client-dashboard">
      <DashHeader title="Client Dashboard" subtitle="Welcome back, Aarav Sharma" />
      <div className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-8 py-8 grid lg:grid-cols-12 gap-6">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          <div className="glass-strong rounded-2xl p-4 sticky top-24">
            <div className="px-3 py-4 border-b border-white/10 mb-2">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-700 flex items-center justify-center font-black">AS</div>
                <div>
                  <div className="font-bold">Aarav Sharma</div>
                  <div className="text-xs text-slate-400">Premium Client · WL-1042</div>
                </div>
              </div>
            </div>
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`client-tab-${t.id}`}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-colors ${
                  tab === t.id ? "bg-blue-600/20 text-blue-300 border border-blue-500/30" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <t.Icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>
        </aside>

        {/* Main */}
        <main className="lg:col-span-9 space-y-6">
          {tab === "portfolio" && <PortfolioTab />}
          {tab === "holdings" && <HoldingsTab />}
          {tab === "sips" && <SIPsTab />}
          {tab === "txn" && <TransactionsTab />}
          {tab === "kyc" && <KYCTab />}
          {tab === "support" && <SupportTab />}
        </main>
      </div>
    </div>
  );
}

export function DashHeader({ title, subtitle }) {
  return (
    <header className="sticky top-0 z-40 glass-strong border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="w-10 h-10 rounded-xl glass flex items-center justify-center hover:bg-white/10" data-testid="dash-back">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-400 font-bold">{title}</div>
            <div className="font-bold text-lg">{subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="w-10 h-10 rounded-xl glass flex items-center justify-center" data-testid="dash-bell">
            <Bell className="w-4 h-4" />
          </button>
          <button className="w-10 h-10 rounded-xl glass flex items-center justify-center" data-testid="dash-settings">
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}

function PortfolioTab() {
  const up = portfolio.todayChg >= 0;
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <KPI label="Portfolio Value" value={fmt(portfolio.totalValue)} sub={`${up ? "+" : ""}${fmt(portfolio.todayChg)} today (${portfolio.todayPct}%)`} subColor={up ? "text-emerald-400" : "text-red-400"} />
        <KPI label="Total Invested" value={fmt(portfolio.invested)} sub="Across 5 schemes" />
        <KPI label="Net Gain" value={fmt(portfolio.gain)} sub={`+${portfolio.gainPct}% absolute`} subColor="text-emerald-400" />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 glass-strong rounded-2xl p-6">
          <div className="flex justify-between mb-3">
            <h3 className="font-bold">Portfolio Growth</h3>
            <span className="text-xs text-slate-500">Last 12 months</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={portfolio.growth}>
              <defs>
                <linearGradient id="gPort" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60A5FA" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#60A5FA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="month" stroke="#475569" fontSize={11} />
              <YAxis stroke="#475569" fontSize={11} tickFormatter={(v) => `${(v / 100000).toFixed(0)}L`} />
              <Tooltip
                contentStyle={{ background: "#0A0F24", border: "1px solid #1E293B", borderRadius: 12 }}
                formatter={(v) => fmt(v)}
              />
              <Area type="monotone" dataKey="value" stroke="#60A5FA" strokeWidth={2.5} fill="url(#gPort)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-2 glass-strong rounded-2xl p-6">
          <h3 className="font-bold mb-3">Asset Allocation</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={portfolio.allocation} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {portfolio.allocation.map((a, i) => (
                  <Cell key={i} fill={a.color} stroke="none" />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: "#0A0F24", border: "1px solid #1E293B", borderRadius: 12 }}
                formatter={(v) => fmt(v)}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-3">
            {portfolio.allocation.map((a) => (
              <div key={a.name} className="flex justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: a.color }} />
                  <span className="text-slate-300">{a.name}</span>
                </span>
                <span className="font-mono text-slate-400">{fmt(a.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function KPI({ label, value, sub, subColor = "text-slate-400" }) {
  return (
    <div className="glass-strong rounded-2xl p-6">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="text-3xl font-black mt-2 font-mono">{value}</div>
      <div className={`text-xs mt-2 ${subColor}`}>{sub}</div>
    </div>
  );
}

function HoldingsTab() {
  return (
    <div className="glass-strong rounded-2xl p-6">
      <div className="flex justify-between mb-4">
        <h3 className="font-bold text-lg">Mutual Fund Holdings</h3>
        <button className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="download-statement">
          <Download className="w-3.5 h-3.5" />
          Statement
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-white/10">
              <th className="py-3">Fund</th>
              <th className="py-3 text-right">Units</th>
              <th className="py-3 text-right">NAV</th>
              <th className="py-3 text-right">Value</th>
              <th className="py-3 text-right">P&L</th>
            </tr>
          </thead>
          <tbody>
            {portfolio.holdings.map((h, i) => (
              <motion.tr
                key={h.name}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.05 }}
                className="border-b border-white/5"
              >
                <td className="py-4 font-semibold">{h.name}</td>
                <td className="py-4 text-right font-mono text-slate-300">{h.units.toFixed(2)}</td>
                <td className="py-4 text-right font-mono text-slate-300">₹{h.nav}</td>
                <td className="py-4 text-right font-mono font-bold">{fmt(h.value)}</td>
                <td className="py-4 text-right font-mono text-emerald-400">+{h.pnl}%</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SIPsTab() {
  return (
    <div className="space-y-4">
      {portfolio.sips.map((s, i) => (
        <div key={i} className="glass-strong rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`sip-row-${i}`}>
          <div>
            <div className="font-bold text-lg">{s.fund}</div>
            <div className="text-xs text-slate-400 mt-1">Started {s.started} · {s.date}</div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-slate-500">Monthly</div>
              <div className="text-xl font-black font-mono">{fmt(s.amount)}</div>
            </div>
            <span className="px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
              {s.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function TransactionsTab() {
  return (
    <div className="glass-strong rounded-2xl p-6">
      <h3 className="font-bold text-lg mb-4">Recent Transactions</h3>
      <div className="space-y-2">
        {portfolio.transactions.map((t, i) => {
          const isOut = t.type === "Switch";
          const Icon = isOut ? ArrowDownRight : ArrowUpRight;
          return (
            <div key={i} className="flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors" data-testid={`txn-${i}`}>
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  t.type === "Dividend" ? "bg-emerald-500/10 text-emerald-400" : "bg-blue-500/10 text-blue-400"
                }`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-semibold">{t.fund}</div>
                  <div className="text-xs text-slate-500">{t.date} · {t.type}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold font-mono">{fmt(t.amount)}</div>
                {t.units > 0 && <div className="text-xs text-slate-500">{t.units.toFixed(2)} units</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KYCTab() {
  return (
    <div className="glass-strong rounded-2xl p-8">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-emerald-400" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold">Verified</div>
          <div className="text-2xl font-black">KYC Status: Active</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {[
          { k: "PAN", v: "ABCDE1234F" },
          { k: "Aadhaar", v: "XXXX-XXXX-4421" },
          { k: "Bank", v: "HDFC ****9821" },
          { k: "Nominee", v: "Mrs. Sharma" },
        ].map((d) => (
          <div key={d.k} className="glass rounded-xl p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">{d.k}</div>
            <div className="font-bold font-mono mt-1">{d.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SupportTab() {
  return (
    <div className="glass-strong rounded-2xl p-8">
      <h3 className="text-2xl font-black mb-2">Raise a Support Ticket</h3>
      <p className="text-slate-400 mb-6">Our team responds within 4 business hours.</p>
      <div className="space-y-4">
        <input className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:border-blue-500 outline-none" placeholder="Subject" data-testid="ticket-subject" />
        <textarea rows={5} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:border-blue-500 outline-none resize-none" placeholder="Describe your issue..." data-testid="ticket-message" />
        <button className="btn-primary" data-testid="ticket-submit">Submit Ticket</button>
      </div>
    </div>
  );
}
